import React, { useEffect, useState, useRef } from 'react';
import { Link, withRouter } from 'react-router-dom';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { connect } from 'react-redux';
import { useTableKeyboardNavigation, Pager } from '@progress/kendo-react-data-tools';
import { classNames, Keys } from '@progress/kendo-react-common';
import { isEmpty } from 'lodash';
import queryString from 'query-string';

import {
    TreeList,
    orderBy,
    filterBy,
    getNestedValue,
    mapTree,
    extendDataItem,
    TreeListTextFilter,
    TreeListNumericFilter,
    treeToFlat
} from '@progress/kendo-react-treelist';
import BobLoadingMask from '../../../components/BobLoadingMask';
import EnumCellFilter from '../../../components/CustomFilters/EnumCellFilter';

import * as BobActions from '../../../actions/bobActions';
import * as DashboardActions from '../../../actions/dashboardActions';

import * as classes from './FaultsTable.module.css';

const subItemsField = 'entities';
const expandField = 'expanded';

const PriorityCell = (props) => {
    const { dataItem } = props;
    const field = props.field || '';
    const cellData = dataItem[field];

    let imgPath = '/assets/images/low.svg';
    if (cellData === 'Medium') {
        imgPath = '/assets/images/medium.svg';
    } else if (cellData === 'High') {
        imgPath = '/assets/images/high.svg';
    }

    return (
        <td>
            <span>
                <img className={classes.PriorityIcon} src={imgPath} alt="" />
                {cellData ? cellData : ''}
            </span>
        </td>
    );
};

const CurrentStatusCell = (props) => {
    const { dataItem } = props;
    const field = props.field || '';
    const cellData = dataItem[field];

    let imgPath = null;
    if (cellData === 'Ok') {
        imgPath = '/assets/images/okIconColor.svg';
    } else if (cellData === 'In Fault') {
        imgPath = '/assets/images/inFaultIconColor.svg';
    } else if (cellData === 'Failure') {
        imgPath = '/assets/images/failedIconColor.svg';
    } else if (cellData === 'In Active') {
        imgPath = '/assets/images/disableIconRedColor.svg';
    }

    return (
        <td>
            <span>
                <img className={classes.PriorityIcon} src={imgPath} alt="" />
                {cellData ? cellData : ''}
            </span>
        </td>
    );
};

const PriorityValues = ['Low', 'Medium', 'High'];
const StatusValues = ['Ok', 'In Fault', 'Failure', 'In Active'];

const PriorityFilter = (props) => {
    return (
        <EnumCellFilter
            {...props}
            data={PriorityValues}
            defaultItem="(All)"
        />
    );
};

const StatusFilter = (props) => {
    return (
        <EnumCellFilter
            {...props}
            data={StatusValues}
            defaultItem="(All)"
        />
    );
};

const TreeListPager = (props) => {
    return (
        <Pager
            style={{
                borderWidth: '1px 1px 1px 1px',
                borderColor: 'rgba(0, 0, 0, 0.12)'
            }}
            pageSizes={[10, 25, 50]}
            className={classes.Pager}
            {...props}
            previousNext={true}
            buttonCount={8}
        />
    );
};

const FaultsTable = ({
    activeNavSite,
    activeNavCompany,
    dashboardEndTimeStamp,
    dashboardStartTimeStamp,
    exportFaultView,
    fetchSingleEquipment,
    fetchSiteFaultSummary,
    history,
    navEquipment,
    refreshTab,
    setRefreshTab,
    setActiveNavEquipment,
    setSelectedRuleFromFaultsPage,
    siteFaultSummary,
    siteFaultSummaryMeta,
    workbookModeEnabled,
    ...props
}) => {
    let _export;
    const gridRef = useRef(null);

    const [selectedRule] = useState(queryString.parse(history.location.search).ruleId);
    const [isLoading, setIsLoading] = useState(false);
    const [state, setState] = useState({
        data:      [...siteFaultSummary],
        dataState: {
            sort:   [],
            filter: [],
        },
        expanded: [selectedRule],
    });

    const [skip, setSkip] = useState(0);
    const [take, setTake] = useState(10);

    useEffect(() => {
        if (gridRef.current && !isEmpty(selectedRule) && !isEmpty(state.data)) {
            const index = gridRef.current?.prevData?.findIndex(d => d.ruleId === selectedRule);
            if (index >= 0) {
                gridRef.current.scrollIntoView({rowIndex: index});
            }
        }
    }, [selectedRule, gridRef.current, state]);

    useEffect(() => {
        if (!workbookModeEnabled) {
            setState({
                ...state,
                dataState: {
                    sort:   [],
                    filter: []
                }
            });
        }
    }, [workbookModeEnabled]);

    useEffect(() => {
        const init = async () => {
            setIsLoading(true);
            await fetchSiteFaultSummary(activeNavCompany.id, activeNavSite.id, dashboardStartTimeStamp, dashboardEndTimeStamp);
            setIsLoading(false);
        };

        if (!isEmpty(activeNavSite)) init();
    }, [activeNavSite]);

    useEffect(() => {
        const init = async () => {
            setIsLoading(true);
            await fetchSiteFaultSummary(activeNavCompany.id, activeNavSite.id, dashboardStartTimeStamp, dashboardEndTimeStamp);
            setIsLoading(false);
        };

        if (!isEmpty(activeNavSite)) init();
    }, [dashboardEndTimeStamp, dashboardStartTimeStamp]);

    useEffect(() => {
        const init = async () => {
            setState({
                ...state,
                data: [...siteFaultSummary]
            });
        };


        if (!isEmpty(siteFaultSummary)) init();
    }, [siteFaultSummary]);

    useEffect(() => {
        if (exportFaultView) {
            exportExcel();
        }
    }, [exportFaultView]);

    useEffect(() => {
        const init = async () => {
            setIsLoading(true);
            await fetchSiteFaultSummary(activeNavCompany.id, activeNavSite.id, dashboardStartTimeStamp, dashboardEndTimeStamp);
            setRefreshTab();
            setIsLoading(false);
        };
        if (refreshTab === 'Faults') init();
    }, [refreshTab]);

    const onExpandChange = (e) => {
        setState({
            ...state,
            expanded: e.value
                ? state.expanded.filter((ruleId) => ruleId !== e.dataItem.ruleId)
                : [...state.expanded, e.dataItem.ruleId],
        });
    };

    const handleDataStateChange = (event) => {
        setState({ ...state, dataState: event.dataState });
    };

    const addExpandField = (dataTree) => {
        const expanded = state.expanded;
        return mapTree(dataTree, subItemsField, (item) =>
            extendDataItem(item, subItemsField, {
                [expandField]: expanded.includes(item.ruleId),
            })
        );
    };

    const processData = () => {
        let { data, dataState } = state;
        let filteredData = filterBy(data, dataState.filter, subItemsField);
        let sortedData = orderBy(filteredData, dataState.sort, subItemsField);
        return addExpandField(sortedData);
    };

    const onPageChange = (event) => {
        const skipVal = event.skip;
        const takeVal = event.take;

        setSkip(skipVal);
        setTake(takeVal);
    };

    const renderScoreNavUrl = dataItem => {
        const queryValues = queryString.parse(history.location.search);

        if (dataItem.entityType === 'equipment') {
            return `equipment?appId=${'ad356bf2-b9db-4bb5-922e-c3394b1a63e3'}&parentId=${'7a88bd3d-64e3-4af9-aa06-9a31533df83c'}&childId=${'4b25aeb8-75d4-4522-9f77-870ab40abb07'}&client=${queryValues.client}&company=${queryValues.company}&equipment=${dataItem.entityId}&site=${queryValues.site}&viewChildId=4b25aeb8-45d4-4522-9f77-870ab40abb07`;
        }
        else {
            return `system?appId=ad356bf2-b9db-4bb5-922e-c3394b1a63e3&childId=383849dd-bd20-4ee2-a0e5-1b192ddc4f81&client=${queryValues.client}&company=${queryValues.company}&parentId=9f215a70-6597-482c-87a4-e515c346d7b3&site=${queryValues.site}&system=${dataItem.entityId}&viewChildId=4b25aeb8-45d4-4522-9f77-870ab40abb24`;
        }

    };

    const handleNav = async dataItem => {

        let path = '/v2/dashboard/' + dataItem.entityType;
        const selectedRule = {
            ruleId:    dataItem.ruleId,
            ruleName:  dataItem.ruleName,
            score:     null,
            sensorIds: []
        };
        setSelectedRuleFromFaultsPage(selectedRule);
        
        const queryValues = queryString.parse(history.location.search);
        if (dataItem.entityType === 'equipment') {
            queryValues.equipment = dataItem.entityId;
            queryValues.parentId = '7a88bd3d-64e3-4af9-aa06-9a31533df83c';
            queryValues.childId = '4b25aeb8-75d4-4522-9f77-870ab40abb07';
            queryValues.viewChildId ='4b25aeb8-45d4-4522-9f77-870ab40abb07';
        }
        else {
            queryValues.system = dataItem.entityId;
            queryValues.parentId = '9f215a70-6597-482c-87a4-e515c346d7b3';
            queryValues.childId = '383849dd-bd20-4ee2-a0e5-1b192ddc4f81';
            queryValues.viewChildId ='4b25aeb8-45d4-4522-9f77-870ab40abb07';
        }

        const queryValuesString = queryString.stringify(queryValues);

        history.push(`${path}?${queryValuesString}`);

        if (dataItem.entityType === 'equipment') {
            const options = {extendedData: true};
            await fetchSingleEquipment(queryValues, options);
        }
    };

    const RenderEquipmentCell = props => {
        const { hasChildren, level = [0], expanded, dataItem, id, ariaColumnIndex} = props;
        const data = getNestedValue(props.field, dataItem);
        const navigationAttributes = useTableKeyboardNavigation(id);
        let dataAsString = '';
    
        const onKeyDownHandler = React.useCallback(
            (event) => {
                if (event.isDefaultPrevented()) { return; }
    
                if (event.keyCode === Keys.enter && props.expandable) {
                    event.preventDefault();
                    props.onExpandChange(event, dataItem, level);
                }
            },
            [props.expandable, dataItem, level]
        );
    
        if (data !== undefined && data !== null) {
            const keys = Object.keys(dataItem);
            const hasEntities = keys.filter(e => e === 'entities').length !== 0;
            if (hasChildren || (hasEntities && dataItem.entities === undefined)) {
                dataAsString = data.toString();
            }
            else {
                // create the link
                dataAsString = 
                    <Link
                        to={renderScoreNavUrl(dataItem)}
                        onClick={
                            (e) => {
                                if (!e.ctrlKey) {
                                    handleNav(dataItem);
                                }
                            }
                        }
                    >
                        {props.dataItem[props.field]}
                    </Link>;
            }
        }
    
        const icons = [];
        if (props.expandable) {
            icons.push(...level.slice(1).map((_x, i) => (<span className="k-icon k-i-none" key={i} />)));
            if (hasChildren) {
                icons.push(
                    <span
                        role="none"
                        className={`k-icon k-i-${expanded ? 'collapse' : 'expand'}`}
                        key="expand-collapse"
                        onClick={event => props.onExpandChange(event, dataItem, level)}
                    />
                );
            } else {
                icons.push(<span className="k-icon k-i-none" key={icons.length} />);
            }
        }
    
        const defaultRendering = (
            <td
                style={props.style}
                // eslint-disable-next-line
                className={classNames(props.className, {['k-text-nowrap']: props.expandable})}
                colSpan={props.colSpan}
                aria-colindex={ariaColumnIndex}
                role={'gridcell'}
                onKeyDown={onKeyDownHandler}
                {...navigationAttributes}
            >
                {icons}
                {dataAsString}
            </td>
        );
    
        return props.render ?
            props.render.call(undefined, defaultRendering, props) :
            defaultRendering;
    };

    const columns = [
        {
            field:      'name',
            title:      'Rule & Equipment Affected',
            width:      '250px',
            filter:     workbookModeEnabled ? TreeListTextFilter : null,
            cell:       RenderEquipmentCell,
            expandable: true,
        },
        {
            field:  'issueTypes',
            title:  'Issue Types',
            width:  '200px',
            filter: workbookModeEnabled ? TreeListTextFilter : null,
        },
        {
            field:      'priority',
            title:      'Priority',
            width:      '200px',
            cell:       PriorityCell,
            filterCell: workbookModeEnabled ? PriorityFilter : null,
        },
        {
            field:  'faultsCount',
            title:  'Fault Count',
            width:  '100px',
            filter: workbookModeEnabled ? TreeListNumericFilter : null,
        },
        {
            field:      'currentStatus',
            title:      'Current Status',
            width:      '100px',
            cell:       CurrentStatusCell,
            filterCell: workbookModeEnabled ? StatusFilter : null
        }
    ];

    const exportExcel = () => {
        _export.save(treeToFlat(processData(), expandField, subItemsField), columns);
    };
    
    return (
        <React.Fragment>
            <ExcelExport ref={exporter => _export = exporter} hierarchy={true}>
                <TreeList
                    ref={gridRef}
                    className={classes.Table}
                    expandField={expandField}
                    subItemsField={subItemsField}
                    onExpandChange={onExpandChange}
                    sortable={{
                        mode: 'multiple',
                    }}
                    {...state.dataState}
                    data={processData()}
                    onDataStateChange={handleDataStateChange}
                    columns={columns}
                />
                <TreeListPager
                    skip={skip}
                    take={take}
                    className={'k-grid-pager'}
                    onPageChange={onPageChange}
                    total={state.data.length}
                />
                {
                    isEmpty(siteFaultSummaryMeta) || isLoading ?
                        <BobLoadingMask />
                        :
                        null
                }
            </ExcelExport>
        </React.Fragment>
    );
};

const mapStateToProps = state => ({
    activeNavClient:         state.bob.activeNavClient,
    activeNavCompany:        state.bob.activeNavCompany,
    activeNavSite:           state.bob.activeNavSite,
    bobLoading:              state.bob.bobLoading,
    companies:               state.core.companies,
    dashboardEndTimeStamp:   state.dashboard.dashboardEndTimeStamp ? state.dashboard.dashboardEndTimeStamp.format() : null,
    dashboardStartTimeStamp: state.dashboard.dashboardStartTimeStamp ? state.dashboard.dashboardStartTimeStamp.format() : null,
    equipmentFilters:        state.dashboard.equipmentFilters,
    navEquipment:            state.bob.navEquipment,
    siteFaultSummary:        state.dashboard.siteFaultSummary,
    siteFaultSummaryMeta:    state.dashboard.siteFaultSummaryMeta
});

const mapDispatchToProps = ({
    ...BobActions,
    ...DashboardActions
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(FaultsTable));
